import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherMultiVehicleDescribeHref,
  buildClaimOtherMultiVehicleDriverHref,
  buildClaimOtherMultiVehicleOtherPartyHref,
  buildClaimOtherMultiVehicleReviewHref,
  buildClaimOtherSingleVehicleCollisionWithHref,
  buildClaimOtherSingleVehicleDescribeHref,
  buildClaimOtherSingleVehicleDriverHref,
  buildClaimOtherSingleVehicleReviewHref,
  buildClaimOtherFireDescribeHref,
  buildClaimOtherFireDriverHref,
  buildClaimOtherFireReviewHref,
  buildClaimOtherMaliciousDamageDescribeHref,
  buildClaimOtherMaliciousDamageReviewHref,
  buildClaimOtherOtherLossDescribeHref,
  buildClaimOtherOtherLossReviewHref,
  buildClaimOtherTheftDescribeHref,
  buildClaimOtherTheftDriverHref,
  buildClaimOtherTheftReviewHref,
  buildClaimOtherWeatherDescribeHref,
  buildClaimOtherWeatherDriverHref,
  buildClaimOtherWeatherReviewHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
  parseTowingAnswerQuery,
} from '../claims/claimFlowModel'
import { parseOtherClaimSubtypeQuery } from '../claims/otherClaimSubtypeOptions'
import {
  isFireDescribeGateSatisfied,
  isFireDriverDetailsGateSatisfied,
  isMaliciousDamageDescribeGateSatisfied,
  isOtherLossDescribeGateSatisfied,
  isMultiVehicleCollisionDetailsGateSatisfied,
  isMultiVehicleDescribeGateSatisfied,
  isMultiVehicleOtherPartyQuestionAnswered,
  isSingleVehicleCollisionTargetGateSatisfied,
  isSingleVehicleDescribeGateSatisfied,
  isSingleVehicleDriverDetailsGateSatisfied,
  isTheftDescribeGateSatisfied,
  isTheftDriverDetailsGateSatisfied,
  isWeatherDescribeGateSatisfied,
  isWeatherDriverDetailsGateSatisfied,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import { useEnsureOtherBranchGstAnswered } from '../claims/useEnsureOtherBranchGstAnswered'

/**
 * Next step after towing (non–multi-vehicle). Multi-vehicle collision, single-vehicle, weather, fire, and theft use dedicated
 * describe/review routes;
 * landing here with those subtypes redirects forward.
 * Non–multi-vehicle: expects `?towing=yes|no` from {@link ClaimTowingQuestion}.
 */
export default function ClaimDetailsPlaceholder() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const towingAnswer = parseTowingAnswerQuery(queryParams.get('towing'))
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null
  const draft = readClaimDraft()
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(draft?.otherClaimSubtypeId ?? null)

  useEnsureOtherBranchGstAnswered({
    claimEntryBranch: 'other',
    policyRef,
    policyTypeQuery: typeInQuery,
    otherClaimSubtype: claimSubtype,
    navPolicy: navState?.policy,
  })

  const isMultiVehicleCollision = claimSubtype === 'multi-vehicle-collision'
  const isSingleVehicle = claimSubtype === 'single-vehicle'
  const isWeatherClaim = claimSubtype === 'weather'
  const isFireClaim = claimSubtype === 'fire'
  const isMaliciousDamageClaim = claimSubtype === 'malicious-damage'
  const isOtherLossClaim = claimSubtype === 'other-loss'
  const isTheftClaim = claimSubtype === 'theft'

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** Multi-vehicle: forward to driver → other party → describe → review. Single-vehicle: driver → collision → describe → review. Otherwise require towing in URL. */
  useEffect(() => {
    if (claimSubtype === null) return
    if (isSingleVehicle) {
      const d = readClaimDraft()
      if (!isSingleVehicleDriverDetailsGateSatisfied(d)) {
        navigate(buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isSingleVehicleCollisionTargetGateSatisfied(d)) {
        navigate(buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isSingleVehicleDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherSingleVehicleDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherSingleVehicleReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isMultiVehicleCollision) {
      const d = readClaimDraft()
      if (!isMultiVehicleCollisionDetailsGateSatisfied(d)) {
        const path = buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, claimSubtype)
        navigate(path, {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isMultiVehicleOtherPartyQuestionAnswered(d)) {
        const path = buildClaimOtherMultiVehicleOtherPartyHref(policyRef, typeInQuery, claimSubtype)
        navigate(path, {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isMultiVehicleDescribeGateSatisfied(d)) {
        const path = buildClaimOtherMultiVehicleDescribeHref(policyRef, typeInQuery, claimSubtype)
        navigate(path, {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      const path = buildClaimOtherMultiVehicleReviewHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isWeatherClaim) {
      const d = readClaimDraft()
      if (!isWeatherDriverDetailsGateSatisfied(d)) {
        navigate(buildClaimOtherWeatherDriverHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isWeatherDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherWeatherDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherWeatherReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isFireClaim) {
      const d = readClaimDraft()
      if (!isFireDriverDetailsGateSatisfied(d)) {
        navigate(buildClaimOtherFireDriverHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isFireDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherFireDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherFireReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isMaliciousDamageClaim) {
      const d = readClaimDraft()
      if (!isMaliciousDamageDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherMaliciousDamageReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isOtherLossClaim) {
      const d = readClaimDraft()
      if (!isOtherLossDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherOtherLossDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherOtherLossReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (isTheftClaim) {
      const d = readClaimDraft()
      if (!isTheftDriverDetailsGateSatisfied(d)) {
        navigate(buildClaimOtherTheftDriverHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      if (!isTheftDescribeGateSatisfied(d)) {
        navigate(buildClaimOtherTheftDescribeHref(policyRef, typeInQuery, claimSubtype), {
          replace: true,
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }
      navigate(buildClaimOtherTheftReviewHref(policyRef, typeInQuery, claimSubtype), {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    if (towingAnswer != null) return
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.towing}?${q}` : CLAIM_FLOW_PATH.towing
    navigate(path, {
      replace: true,
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }, [
    claimSubtype,
    isMultiVehicleCollision,
    isSingleVehicle,
    isWeatherClaim,
    isFireClaim,
    isMaliciousDamageClaim,
    isOtherLossClaim,
    isTheftClaim,
    navigate,
    navState?.policy,
    policyRef,
    towingAnswer,
    typeInQuery,
  ])

  const backToPreviousStep = () => {
    if (claimSubtype === null) return
    if (isMultiVehicleCollision) {
      const d = readClaimDraft()
      const path = !isMultiVehicleCollisionDetailsGateSatisfied(d)
        ? buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, claimSubtype)
        : !isMultiVehicleOtherPartyQuestionAnswered(d)
          ? buildClaimOtherMultiVehicleOtherPartyHref(policyRef, typeInQuery, claimSubtype)
          : buildClaimOtherMultiVehicleDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isSingleVehicle) {
      const d = readClaimDraft()
      const path = !isSingleVehicleDriverDetailsGateSatisfied(d)
        ? buildClaimOtherSingleVehicleDriverHref(policyRef, typeInQuery, claimSubtype)
        : !isSingleVehicleCollisionTargetGateSatisfied(d)
          ? buildClaimOtherSingleVehicleCollisionWithHref(policyRef, typeInQuery, claimSubtype)
          : buildClaimOtherSingleVehicleDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isWeatherClaim) {
      const path = buildClaimOtherWeatherDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isFireClaim) {
      const path = buildClaimOtherFireDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isMaliciousDamageClaim) {
      const path = buildClaimOtherMaliciousDamageDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isOtherLossClaim) {
      const path = buildClaimOtherOtherLossDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    if (isTheftClaim) {
      const path = buildClaimOtherTheftDescribeHref(policyRef, typeInQuery, claimSubtype)
      if (navState?.policy) {
        navigate(path, { state: { policy: navState.policy } })
      } else {
        navigate(path)
      }
      return
    }
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    if (claimSubtype) p.set(OTHER_CLAIM_SUBTYPE_QUERY_KEY, claimSubtype)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.towing}?${q}` : CLAIM_FLOW_PATH.towing
    if (navState?.policy) {
      navigate(path, { state: { policy: navState.policy } })
    } else {
      navigate(path)
    }
  }

  const showLoadingGate =
    claimSubtype !== null &&
    (isMultiVehicleCollision ||
    isSingleVehicle ||
    isWeatherClaim ||
    isFireClaim ||
    isMaliciousDamageClaim ||
    isOtherLossClaim ||
    isTheftClaim
      ? true
      : towingAnswer == null)

  if (showLoadingGate) {
    return (
      <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
        <DashboardNavigation />
        <ClaimsYellowHeader
          backAriaLabel="Back"
          onBack={backToPreviousStep}
          title="Further details"
        />
        <ClaimsFlowMain>
          <FormCard>
            <p className="m-0 font-body text-sm leading-sm text-text-tertiary-600">Loading…</p>
          </FormCard>
        </ClaimsFlowMain>
        <Footer />
      </div>
    )
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToPreviousStep}
        title="Further details"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={formatClaimPolicySummaryLine(
              navState?.policy ?? (policyRef ? { policyRef } : null)
            )}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            towingAnswer={
              isMultiVehicleCollision ||
              isWeatherClaim ||
              isFireClaim ||
              isMaliciousDamageClaim ||
              isOtherLossClaim ||
              isTheftClaim ||
              isSingleVehicle
                ? null
                : towingAnswer
            }
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            weatherClaimConditionsDescription={draft?.weatherClaimConditionsDescription ?? null}
            fireClaimTypeDescription={draft?.fireClaimTypeDescription ?? null}
            theftOrBreakInDetailDescription={draft?.theftOrBreakInDetailDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(draft)}
            singleVehicleIncidentDescription={draft?.singleVehicleIncidentDescription ?? null}
            singleVehicleDamageDescription={draft?.singleVehicleDamageDescription ?? null}
          />
        }
      >
        <FormCard>
          <p className="m-0 font-body text-sm leading-sm text-text-tertiary-600">
            More questions about your claim will appear here. If you need help now, call{' '}
            <span className="font-semibold text-text-primary-900">1300 296 296</span>.
          </p>
        </FormCard>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
